import type { ZodEffects, ZodNullable, ZodOptional, ZodString } from 'zod';
import { Address4 } from 'ip-address';
import { BigInteger } from 'jsbn';
import * as z from 'zod';

import { isDefined } from '../helpers/isDefined';
import { truthy } from '../helpers/utils/truthy';

export { Address4 };

export { BigInteger };

export const ZERO_CIDR = '0.0.0.0/0';

export function safeParseAddress4(
  addr: string | null | undefined,
  prefixLength?: number,
): Address4 | null {
  if (!addr) return null;

  const addrString = prefixLength !== undefined ? `${addr}/${prefixLength}` : addr;

  try {
    return new Address4(addrString);
  } catch (e) {
    return null;
  }
}

export function formatSubnetWithNetmask(
  ip: string | null | undefined,
  prefixLength: number | null | undefined,
): string | undefined {
  if (!ip || prefixLength == null) return undefined;
  const addr = safeParseAddress4(`${ip}/${prefixLength}`);
  return addr ? `${addr.startAddress().address}/${addr.subnetMask}` : undefined;
}

// From https://pkg.go.dev/net/netip#Addr.IsPrivate
export const PRIVATE_PREFIXES = ['10.0.0.0/8', '172.16.0.0/12', '192.168.0.0/16'] as const;

export function isPrivateAddress4(addr: Address4): boolean {
  return PRIVATE_PREFIXES.some((privatePrefix) => addr.isInSubnet(new Address4(privatePrefix)));
}

export function isPrivateAddress(addrString: string | null | undefined): boolean {
  const addr = safeParseAddress4(addrString);
  return addr != null && isPrivateAddress4(addr);
}

export const TEST_NET_PREFIXES = ['203.0.113.0/24'] as const;

// https://en.wikipedia.org/wiki/Reserved_IP_addresses
// TEST-NET-{1,2,3}, explicitly only allowing 3 for now
function isTESTNETAddress(addr: Address4): boolean {
  return TEST_NET_PREFIXES.some((privatePrefix) => addr.isInSubnet(new Address4(privatePrefix)));
}

export function isPrivateOrTESTNETAddress4(addr: Address4): boolean {
  return isPrivateAddress4(addr) || isTESTNETAddress(addr);
}

export function getCIDRFromAddressString(addrString: string): string | null {
  const addr = safeParseAddress4(addrString);
  if (!addr) return null;
  const start = addr.startAddress();
  return `${start.address}${addr.subnet}`;
}

/**
 * If the IP range of the subnet contains only one address (i.e. is /32), returns the address.
 * Otherwise, returns the second address in the range.
 */
export function getFirstUsableAddress(addr: Address4) {
  return addr.subnet === '/32' ? addr.endAddress() : addr.startAddressExclusive();
}

/**
 * If the IP range of the subnet contains only one address, returns that
 * address. If it contains two addresses, returns the second address. Otherwise,
 * returns the second-to-last address in the range.
 */
export function getLastUsableAddress(addr: Address4) {
  return ['/31', '/32'].includes(addr.subnet) ? addr.endAddress() : addr.endAddressExclusive();
}

export function refineIPv4String(val: string, ctx: z.RefinementCtx) {
  const address = safeParseAddress4(val);
  if (!isDefined(address)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Must be a valid IP address like xx.xx.xx.xx',
      fatal: true,
    });
    return false;
  }
  return true;
}

type OptionalOrRequiredString =
  | ZodEffects<
      ZodOptional<ZodNullable<ZodString>>,
      string | null | undefined,
      string | null | undefined
    >
  | ZodEffects<ZodString, string, string>;
export function refineIPV4Range(
  v: OptionalOrRequiredString,
  requireTruthy = true,
  minPrefixLength?: number,
) {
  return v
    .refine(
      (val) => {
        if (!requireTruthy && !truthy(val)) return true;
        const address = safeParseAddress4(val)!;
        return address.correctForm() === address.startAddress().correctForm();
      },
      (val) => {
        if (!requireTruthy && !truthy(val)) return {};
        const address = safeParseAddress4(val)!;
        return {
          message: `Must be the lowest IP represented by the range. Expected ${address
            .startAddress()
            .correctForm()}.`,
        };
      },
    )
    .refine((val) => {
      if (!requireTruthy && !truthy(val)) return true;
      return safeParseAddress4(val)!.subnet !== '/32';
    }, 'Must not be a single IP address or /32 subnet')
    .refine(
      (val) => {
        if (!minPrefixLength || (!requireTruthy && !truthy(val))) return true;
        return safeParseAddress4(val)!.subnetMask >= minPrefixLength;
      },
      minPrefixLength ? `Must be a /${minPrefixLength} or smaller subnet` : '',
    );
}

export function validIPv4String() {
  return z.string().superRefine(refineIPv4String);
}

export function validIPv4Range() {
  return refineIPV4Range(validIPv4String());
}

export interface Address4Range {
  start: Address4;
  end: Address4;
}

export function subnetToRange(ip: Address4): Address4Range {
  return {
    start: ip.startAddress(),
    end: ip.endAddress(),
  };
}

export function addressPlus(addr: Address4, step: number): Address4 {
  return Address4.fromBigInteger(addr.bigInteger().add(new BigInteger(step.toString())));
}

export function addressRangeWithSize(start: Address4, size: number): Address4Range {
  return {
    start,
    end: addressPlus(start, size - 1),
  };
}

export function isValidIPRange(range: Address4Range): boolean {
  return range.start.bigInteger().intValue() <= range.end.bigInteger().intValue();
}

export function ipRangesOverlap(range1: Address4Range, range2: Address4Range): boolean {
  return (
    range1.start.bigInteger().intValue() <= range2.end.bigInteger().intValue() &&
    range2.start.bigInteger().intValue() <= range1.end.bigInteger().intValue()
  );
}

export function isIPWithinRange(ip: Address4, range: Address4Range): boolean {
  return ip.bigInteger() >= range.start.bigInteger() && ip.bigInteger() <= range.end.bigInteger();
}

export function isIPWithinSubnet(ip: Address4, cidr: string | Address4): boolean {
  const subnet = typeof cidr === 'string' ? safeParseAddress4(cidr) : cidr;
  if (!subnet) return false;

  return ip.isInSubnet(subnet);
}

export function isIPStringWithinSubnet(ipStr: string, cidr: string | Address4): boolean {
  const ip = safeParseAddress4(ipStr);
  const subnet = typeof cidr === 'string' ? safeParseAddress4(cidr) : cidr;
  if (!subnet || !ip) return false;

  return ip.isInSubnet(subnet);
}

export function splitCIDR(cidr: string): [string, number] | undefined {
  const pieces = cidr.split('/');
  if (pieces.length !== 2) return undefined;

  const [address, lengthStr] = pieces;
  const prefixLength = Number.parseInt(lengthStr, 10);
  if (Number.isNaN(prefixLength) || !Number.isInteger(prefixLength)) return undefined;

  return [address, prefixLength];
}

export function isValidCIDRString(cidr: string): boolean {
  const addr = safeParseAddress4(cidr);
  return !!addr && cidr === `${addr.startAddress().address}/${addr.subnetMask}`;
}

/**
 * Appends /32 to a bare IPv4 address so single IPs are accepted where CIDRs are required;
 * anything else is returned trimmed, unchanged.
 */
export function normalizeCIDRInput(value: string): string {
  const trimmed = value.trim();
  if (!trimmed.includes('/') && safeParseAddress4(trimmed)) {
    return `${trimmed}/32`;
  }
  return trimmed;
}

/** Must match the firmware's limit on how many CIDRs a single pattern can expand to. */
const MAX_IP_ADDRESS_PATTERN_EXPANSION = 16384;

const DECIMAL_REGEX = /^\d+$/;

function parsePatternOctet(value: string): number | undefined {
  if (!DECIMAL_REGEX.test(value)) return undefined;
  const parsed = Number.parseInt(value, 10);
  return parsed <= 255 ? parsed : undefined;
}

export function isValidIPAddressPattern(value: string): boolean {
  const pieces = value.split('/');
  if (pieces.length !== 2) return false;
  const [address, bitsStr] = pieces;

  if (!DECIMAL_REGEX.test(bitsStr)) return false;
  const bits = Number.parseInt(bitsStr, 10);
  if (bits > 32) return false;

  const octets = address.split('.');
  if (octets.length !== 4) return false;

  let isPattern = false;
  let count = 1;
  for (const [i, octet] of octets.entries()) {
    let lo: number | undefined;
    let hi: number | undefined;
    if (octet === '*') {
      lo = 0;
      hi = 255;
    } else {
      const rangePieces = octet.split('-');
      if (rangePieces.length > 2) return false;
      lo = parsePatternOctet(rangePieces[0]);
      hi = rangePieces.length === 2 ? parsePatternOctet(rangePieces[1]) : lo;
    }
    if (lo === undefined || hi === undefined || lo > hi) return false;
    if (lo !== hi) isPattern = true;

    const maskBits = Math.min(Math.max(bits - 8 * i, 0), 8);
    // eslint-disable-next-line no-bitwise
    const mask = (0xff << (8 - maskBits)) & 0xff;
    // eslint-disable-next-line no-bitwise
    count *= ((hi & mask) - (lo & mask)) / (256 - mask) + 1;
  }

  return isPattern && count <= MAX_IP_ADDRESS_PATTERN_EXPANSION;
}

export function isValidCIDR(ip: string, prefixLength: number): boolean {
  return isValidCIDRString(`${ip}/${prefixLength}`);
}

export function isSubnetOverlapping(subnet1: Address4, subnet2: Address4): boolean {
  return subnet1.isInSubnet(subnet2) || subnet2.isInSubnet(subnet1);
}

/**
 * Note that this assumes existingAddrs is in ascending order
 */
export function getNextAvailableSubnet(
  startAddr: Address4,
  existingAddrs: Address4[],
): Address4 | null {
  let addr: Address4 | null = startAddr;

  const step = new BigInteger((2 ** (32 - addr.subnetMask)).toString());

  for (let i = 0; i < existingAddrs.length && addr; ) {
    if (isSubnetOverlapping(addr, existingAddrs[i])) {
      const newAddressWithoutPrefix = Address4.fromBigInteger(addr.bigInteger().add(step));
      addr = safeParseAddress4(`${newAddressWithoutPrefix.address}/${addr.subnetMask}`);
    } else {
      i += 1;
    }
  }

  return addr;
}

export function getOverlappingAddresses(
  startAddr: Address4,
  existingAddrs: Address4[],
): Address4[] {
  return existingAddrs.filter((existingAddr) => isSubnetOverlapping(startAddr, existingAddr));
}
